import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import throttle from 'lodash/throttle';
import cloneDeep from 'lodash/cloneDeep';
import {
  isNumeric,
  createCustomPropType,
  isUuid,
  isNumericFormatted,
} from '../../../js/app/react/validators';
import Modal from '../../CommonComponents/AdvModal';
import AdvSlider from '../../CommonComponents/AdvSlider';
import AdvInput from '../../CommonComponents/AdvInput';
import AdvInputWrapper from '../../CommonComponents/AdvInputWrapper';
import Loading from '../Loading/LoadingView';
import AdvForm from '../../CommonComponents/AdvForm';
import { ADDITIONAL_SAVINGS_LABEL_DICTIONARY } from '../WhatIfsConstants';

class CompareGoalsView extends Component {
  static defaultImpact(goalsAdditionalSavingsWhatIf) {
    return (
      (((goalsAdditionalSavingsWhatIf.WhatIfPlanImpact || [{}])[0] || {}).Value || [{}])[0] || {}
    );
  }
  static planImpactLabel(goalsAdditionalSavingsWhatIf) {
    return CompareGoalsView.defaultImpact(goalsAdditionalSavingsWhatIf).Label || '';
  }

  static planImpactProposed(goalsAdditionalSavingsWhatIf) {
    return CompareGoalsView.defaultImpact(goalsAdditionalSavingsWhatIf).Proposed || '';
  }

  static planImpactCurrent(goalsAdditionalSavingsWhatIf) {
    return CompareGoalsView.defaultImpact(goalsAdditionalSavingsWhatIf).Current || '';
  }

  constructor() {
    super();
    this.throttledFetchPlanImpact = throttle(this.throttledFetchPlanImpact.bind(this), 1000);
    this.onSlide = this.onSlide.bind(this);
    this.remainingCashflow = this.remainingCashflow.bind(this);
    this.formattedRemainingCashflow = this.formattedRemainingCashflow.bind(this);
    this.cashflowClassName = this.cashflowClassName.bind(this);
    this.cashflowLabel = this.cashflowLabel.bind(this);
    this.state = {
      sliders: [],
    };
  }

  UNSAFE_componentWillMount() {
    this.setState({
      sliders: this.props.goalsAdditionalSavingsWhatIfs.map(({ WhatIfPanelItem }) => ({
        sliderValue: +WhatIfPanelItem.WhatIf.AllocatedCashflow,
      })),
    });
  }

  componentDidMount() {
    const { fetchCompareGoals, clientId, fpId, goalsAdditionalSavingsWhatIfs } = this.props;
    if (goalsAdditionalSavingsWhatIfs.length) {
      return;
    }
    fetchCompareGoals(clientId, fpId);
  }

  UNSAFE_componentWillReceiveProps(next) {
    if (!isEqual(this.props.goalsAdditionalSavingsWhatIfs, next.goalsAdditionalSavingsWhatIfs)) {
      this.setState({
        sliders: next.goalsAdditionalSavingsWhatIfs.map(({ WhatIfPanelItem }) => ({
          sliderValue: +WhatIfPanelItem.WhatIf.AllocatedCashflow,
        })),
      });
    }
  }

  componentDidUpdate(previousProps) {
    if (previousProps === this.props) {
      return;
    }
    const impactTriggerProps = ['whatIfsNoImpactsOrDefault', 'fpId'];

    if (
      previousProps.whatIfsNoImpactsOrDefault.length === 0 ||
      this.props.whatIfsNoImpactsOrDefault.length === 0
    ) {
      return;
    }

    if (!isEqual(pick(this.props, impactTriggerProps), pick(previousProps, impactTriggerProps))) {
      this.throttledFetchPlanImpact();
    }
  }

  onSlide(index) {
    return value => {
      const sliders = cloneDeep(this.state.sliders);
      sliders[index].sliderValue = value;
      this.setState({
        sliders,
      });
    };
  }

  onAdditionalSavingsBlur(index) {
    return valueArg => {
      const {
        clientId,
        store,
        goalId,
        whatIfKey,
        whatIfType,
        goalsAdditionalSavingsWhatIfs,
        maxCashflow,
      } = this.props;
      let value = valueArg;
      if (isNumericFormatted(valueArg)) {
        if (typeof valueArg === 'string') {
          value = +valueArg.replace(/[,]/g, '');
        }
      } else {
        value = 0;
      }
      if (value > maxCashflow) {
        value = Math.max(maxCashflow, 0);
      }
      store(
        `GoalsAdditionalSavingsWhatIfs[${index}].WhatIfPanelItem.WhatIf.AllocatedCashflow`,
        clientId,
        goalId,
        whatIfKey,
        whatIfType,
        goalsAdditionalSavingsWhatIfs[index].WhatIfPanelItem.WhatIf.WhatIfKey,
      )(value);
    };
  }

  throttledFetchPlanImpact() {
    const {
      clientId,
      fpId,
      goalId,
      whatIfType,
      whatIfKey,
      goalsAdditionalSavingsWhatIfs,
      fetchAdditionalSavingsPlanImpact,
    } = this.props;
    fetchAdditionalSavingsPlanImpact(
      clientId,
      fpId,
      goalId,
      whatIfType,
      whatIfKey,
      goalsAdditionalSavingsWhatIfs,
    );
  }

  remainingCashflow(value) {
    const { availableCashflow } = this.props;
    return Math.trunc(availableCashflow - value);
  }

  cashflowClassName(value) {
    if (this.remainingCashflow(value) < 0) {
      return 'what-ifs-compare-goals-popup-header-remaining negative';
    }
    return 'what-ifs-compare-goals-popup-header-remaining positive';
  }

  cashflowLabel(value) {
    const remainingCashflowValue = this.remainingCashflow(value);
    if (remainingCashflowValue < 0) {
      return 'per month shortage';
    }
    if (remainingCashflowValue === 0) {
      return 'remaining';
    }
    return 'per month remaining';
  }

  formattedRemainingCashflow(value) {
    const { ADV } = window;
    const valueToFormat = Math.abs(this.remainingCashflow(value));
    if (valueToFormat > 99999) {
      return `${ADV.util.short_number_format(Math.abs(this.remainingCashflow(value)))}`;
    }
    return `${ADV.util.number_format(Math.abs(this.remainingCashflow(value)))}`;
  }

  render() {
    const {
      onDismiss,
      maxCashflow,
      clientId,
      fpId,
      popupLoading,
      store,
      goalId,
      whatIfKey,
      whatIfType,
      optimizeGoals,
      goalsAdditionalSavingsWhatIfs,
    } = this.props;
    const { sliders } = this.state;
    const sliderValuesSum = sliders.reduce(
      (accumulator, current) => accumulator + Math.round(+current.sliderValue),
      0,
    );
    const { ADV } = window;

    return (
      <Modal
        className="popup what-ifs-compare-goals-popup"
        isOpen
        onRequestClose={onDismiss(clientId)}
        overlayClassName="closerdiv"
        shouldCloseOnOverlayClick={false}
      >
        {popupLoading && <Loading />}
        <div className="what-ifs-compare-goals-popup-header">
          <div className="what-ifs-compare-goals-popup-header-title">Compare Goals</div>
          <div className={this.cashflowClassName(sliderValuesSum)}>
            ${this.formattedRemainingCashflow(sliderValuesSum)}{' '}
            {this.cashflowLabel(sliderValuesSum)}
          </div>
          <button
            type="button"
            className="what-ifs-compare-goals-popup-header-optimize outline"
            onClick={optimizeGoals(
              clientId,
              fpId,
              goalId,
              whatIfKey,
              whatIfType,
              goalsAdditionalSavingsWhatIfs,
            )}
          >
            <i className="fa fa-sync-alt" /> Optimize
          </button>

          <span className="what-ifs-compare-goals-popup-header-close">
            <i
              className="fa fa-times what-ifs-compare-goals-popup-header-close-button"
              onClick={onDismiss(clientId)}
              role="button"
              tabIndex={0}
              onKeyDown={() => {}}
            />
          </span>
        </div>
        <AdvForm className="what-ifs-compare-goals-popup-container">
          <input type="hidden" value={ADV.util.guid()} />
          <ul className="what-ifs-compare-goals-ul adv-scrollbars">
            {goalsAdditionalSavingsWhatIfs.map((goalsAdditionalSavingsWhatIf, index) => (
              <li
                key={goalsAdditionalSavingsWhatIf.WhatIfPanelItem.WhatIf.WhatIfKey}
                className="what-ifs-compare-goals-li"
              >
                <div className="what-ifs-compare-goals-li-heading">
                  <div className="what-ifs-compare-goals-li-inputs what-ifs-compare-goals-plan-impact-heading-goal-name">
                    {goalsAdditionalSavingsWhatIf.GoalName}
                  </div>
                  <div className="what-ifs-compare-goals-li-current what-ifs-compare-goals-plan-impact-heading">
                    Current
                  </div>
                  <div className="what-ifs-compare-goals-li-proposed what-ifs-compare-goals-plan-impact-heading">
                    Proposed
                  </div>
                </div>
                <div className="what-ifs-compare-goals-li-content">
                  <div className="what-ifs-compare-goals-li-inputs">
                    <div className="what-ifs-compare-goals-li-input-additional-savings">
                      <span className="what-ifs-cg-additional-savings-label">
                        {ADDITIONAL_SAVINGS_LABEL_DICTIONARY[
                          goalsAdditionalSavingsWhatIf.WhatIfPanelItem.WhatIf.WhatIfType
                        ] || 'Additional Savings'}
                      </span>
                      <AdvInputWrapper
                        hasPerMonth
                        hasCurrency
                        className="what-ifs-compare-goals-input-wrapper what-ifs-cg-additional-savings-input"
                      >
                        <AdvInput
                          autoComplete="off"
                          type="text"
                          dataType="money"
                          maxLength="14"
                          onBlur={this.onAdditionalSavingsBlur(index)}
                          value={ADV.util.formatValue(sliders[index].sliderValue, 'money')}
                          className="what-ifs-compare-goals-input"
                        />
                      </AdvInputWrapper>
                    </div>
                    <AdvSlider
                      className="what-ifs-compare-goals-slider light"
                      min={0}
                      max={maxCashflow}
                      value={goalsAdditionalSavingsWhatIf.WhatIfPanelItem.WhatIf.AllocatedCashflow}
                      onChange={value => {
                        store(
                          `GoalsAdditionalSavingsWhatIfs[${index}].WhatIfPanelItem.WhatIf.AllocatedCashflow`,
                          clientId,
                          goalId,
                          whatIfKey,
                          whatIfType,
                          goalsAdditionalSavingsWhatIf.WhatIfPanelItem.WhatIf.WhatIfKey,
                        )(value);
                      }}
                    />
                  </div>
                  <div className="what-ifs-compare-goals-li-current what-ifs-compare-goals-plan-impact-item">
                    <div className="what-ifs-compare-goals-li-plan-impact-title">
                      {CompareGoalsView.planImpactLabel(goalsAdditionalSavingsWhatIf)}
                    </div>
                    <div className="what-ifs-compare-goals-li-plan-impact-content">
                      {CompareGoalsView.planImpactCurrent(goalsAdditionalSavingsWhatIf)}
                    </div>
                  </div>
                  <div className="what-ifs-compare-goals-li-proposed what-ifs-compare-goals-plan-impact-item">
                    <div className="what-ifs-compare-goals-li-plan-impact-title">
                      {CompareGoalsView.planImpactLabel(goalsAdditionalSavingsWhatIf)}
                    </div>
                    <div className="what-ifs-compare-goals-li-plan-impact-content">
                      {CompareGoalsView.planImpactProposed(goalsAdditionalSavingsWhatIf)}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </AdvForm>
      </Modal>
    );
  }
}
CompareGoalsView.defaultProps = {
  availableCashflow: 0,
  maxCashflow: 10000,
};

CompareGoalsView.propTypes = {
  clientId: createCustomPropType(isNumeric).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  goalId: createCustomPropType(isUuid).isRequired,
  fetchCompareGoals: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  availableCashflow: PropTypes.number,
  maxCashflow: PropTypes.number,
  store: PropTypes.func.isRequired,
  fetchAdditionalSavingsPlanImpact: PropTypes.func.isRequired,
  goalsAdditionalSavingsWhatIfs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  whatIfsNoImpactsOrDefault: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  popupLoading: PropTypes.bool.isRequired,
  whatIfType: createCustomPropType(isNumeric).isRequired,
  whatIfKey: PropTypes.string.isRequired,
  optimizeGoals: PropTypes.func.isRequired,
};

export default CompareGoalsView;
